import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { supabase } from '../lib/db';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

interface Item {
  id: string;
  name: string;
  description: string;
  price: number;
  new_price: number | null;
  product_number: string;
  item_images: Array<{ image_url: string }>;
}

export const PreviewSharePage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useLanguage();
  const { item_id } = useParams<{ item_id: string }>();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('PreviewSharePage mounted:', new Date().toISOString());
    console.log('Item ID from URL:', item_id);

    const loadItem = async () => {
      try {
        console.log('Fetching item data from Supabase...');
        const { data, error } = await supabase
          .from('items')
          .select('*')
          .eq('id', item_id)
          .single();

        console.log('Supabase response:', { success: !!data, error: error?.message });

        if (error) {
          console.error('Error loading item:', error.message);
          throw error;
        }

        setItem(data);
      } catch (error) {
        console.error('Failed to load item:', error);
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [item_id]);

  useEffect(() => {
    if (!loading && item) {
      // Get the branch_link and delay from URL parameters
      const params = new URLSearchParams(location.search);
      const branchLink = params.get('branch_link');
      const delay = parseInt(params.get('delay') || '5000', 10); // Default to 5 seconds if not specified

      if (branchLink) {
        // Show a countdown message
        const countdownElement = document.getElementById('redirect-countdown');
        if (countdownElement) {
          let timeLeft = delay / 1000;
          const countdownInterval = setInterval(() => {
            timeLeft--;
            if (countdownElement) {
              countdownElement.textContent = `${timeLeft}...`;
            }
            if (timeLeft <= 0) {
              clearInterval(countdownInterval);
            }
          }, 1000);
        }

        // Redirect after the specified delay
        const redirectTimer = setTimeout(() => {
          try {
            window.location.href = decodeURIComponent(branchLink);
          } catch (error) {
            console.error('Error redirecting:', error);
            // If there's an error with the URL, show an error message
            const errorElement = document.getElementById('redirect-error');
            if (errorElement) {
              errorElement.textContent = 'Error redirecting to the app. Please try the link below.';
              errorElement.style.display = 'block';
            }
          }
        }, delay);

        return () => {
          clearTimeout(redirectTimer);
          if (countdownElement) {
            countdownElement.textContent = '';
          }
        };
      }
    }
  }, [loading, item, location.search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('error')}</h1>
          <p className="text-gray-600">{error || t('itemNotFoundDescription')}</p>
        </div>
      </div>
    );
  }

  // Get the first image URL or use a default image
  const imageUrl = item.item_images?.[0]?.image_url || 'https://warkashop.com/default-item-image.jpg';
  const price = item.new_price ?? item.price;
  const description = `${item.product_number ? `Product Number: ${item.product_number}\n` : ''}Price: ${price} Br\n${item.description}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Helmet>
        <title>{item?.name || 'Item Preview'} | WarkaShop</title>
        <meta name="description" content={description || ''} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={item?.name || 'Item Preview'} />
        <meta property="og:description" content={description || ''} />
        <meta property="og:image" content={imageUrl || ''} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={item?.name || 'Item Preview'} />
        <meta name="twitter:description" content={description || ''} />
        <meta name="twitter:image" content={imageUrl || ''} />
      </Helmet>

      {loading ? (
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      ) : error || !item ? (
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('error')}</h1>
          <p className="text-gray-600">{error || t('itemNotFoundDescription')}</p>
        </div>
      ) : (
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="relative aspect-square">
            <img
              src={imageUrl}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{item.name}</h1>
            <p className="text-gray-600 mb-4">{description}</p>
            <div className="text-center text-gray-500">
              <p>{t('redirectingToApp')} <span id="redirect-countdown"></span></p>
              <p id="redirect-error" className="text-red-500 hidden"></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 